import { formatDate } from '../utils/utils';
import { DATEPICKER_MODES } from '../components/SectionComponents/SamCalendar/SamCalendar.vue';

const isCalendarModeValid = (value) => Object.values(DATEPICKER_MODES).includes(value);

export default {
  props: {
    /**
     * Props to be bound to SamMenu component. See SamMenu component props.
     */
    menuProps: {
      type: Object,
      default: () => ({
        closeOnContentClick: false,
      }),
    },
    /**
     * Props to be bound to SamCalendar component. See SamCalendar component props.
     */
    calendarProps: {
      type: Object,
      default: () => ({}),
    },
    /**
     * Calendar mode to be assigned
     * @values "single", "range", "multiple"
     */
    mode: {
      type: String,
      default: null,
      validator: isCalendarModeValid,
    },
    /**
     * The component's model value
     * @model
     */
    value: {
      type: [Date, Array],
      default: null,
    },
    /**
     * The date format to be displayed in the input. It accepts flatpickr date formats.
     */
    displayDateFormat: {
      type: String,
      default: null,
    },
    /**
     * If the Calendar should be for dates only
     * @values true, false
     */
    onlyDate: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * If the Calendar should be for time only
     * @values true, false
     */
    onlyTime: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Disables the input
     * @values true, false
     */
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * If the Select should display a clear input button
     * @values true, false
     */
    withClear: {
      type: Boolean,
      required: false,
      default: true,
    },
    /**
     * Minimum selectable date for the calendar (inclusive).
     * Should be a Date instance or null.
     */
    minDate: {
      type: Date,
      required: false,
      default: null,
    },

    /**
     * Maximum selectable date for the calendar (inclusive).
     * Should be a Date instance or null.
     */
    maxDate: {
      type: Date,
      required: false,
      default: null,
    },
  },
  computed: {
    datepickerMode() {
      if (isCalendarModeValid(this.mode)) return this.mode; // mode prop has higher priority
      if (isCalendarModeValid(this.calendarProps?.mode)) return this.calendarProps.mode;
      return DATEPICKER_MODES.SINGLE; // sets default if not valid
    },
    calendarBoundProps() {
      return {
        ...this.calendarProps,
        mode: this.datepickerMode,
        minDate: this.minDate ?? this.calendarProps?.minDate,
        maxDate: this.maxDate ?? this.calendarProps?.maxDate,
      };
    },
    displayDate() {
      // for single mode
      if (this.datepickerMode === DATEPICKER_MODES.SINGLE) return this.formatDate(this.value);

      // for multiple and range modes
      const formattedDates = this.value?.map?.(this.formatDate).filter?.((date) => !!date);

      if (!formattedDates?.length) return null;

      // for multiple mode
      if (this.datepickerMode === DATEPICKER_MODES.MULTIPLE)
        return formattedDates.join(`${this.calendarBoundProps?.multipleModeSeparator ?? ', '}`);

      // for ranged mode
      if (this.datepickerMode === DATEPICKER_MODES.RANGE) {
        const [startDate, endDate] = this.value;

        const hasSameDateAndTime = startDate?.getTime() === endDate?.getTime();
        const hasSameDate = startDate?.toDateString() === endDate?.toDateString();

        if (hasSameDateAndTime || (this.onlyDate && hasSameDate)) {
          return this.formatDate(startDate);
        }

        return formattedDates.join(' to ');
      }

      // for sanity
      return null;
    },
  },
  methods: {
    onCalendarDateChange(datepickerPayload) {
      this.$emit('change', datepickerPayload);
      this.$emit('input', datepickerPayload);
    },
    onMenuOpen(menuPayload) {
      this.$emit('open', menuPayload);
    },
    onMenuClose(menuPayload) {
      this.$emit('close', menuPayload);
    },
    onInputClear($event) {
      $event.stopPropagation();
      this.$emit('change', Array.isArray(this.value) ? [] : null);
      this.$emit('input', Array.isArray(this.value) ? [] : null);
      this.$refs.calendar.clear();
    },
    formatDate(date) {
      return formatDate(date, this.dateFormat);
    },
  },
};
